import type { FC, ReactNode } from 'react';
import type { IEvaultApi } from './use-evault-api';
import type { IRetentionCatalogue, IRetentionSchedule, IVaultDocument, IVaultSource } from './types';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import { REACT_APP_API_HOST as API_HOST } from 'envs';
import { useNotification } from 'hooks';
import {
	CopyRow,
	Drawer,
	DrawerHead,
	KV,
	Section,
	StatusChip,
} from 'views/design-system';

import {
	CHECK_CHIP,
	OVERALL_CHIP,
	canActAsOwner,
	dayOf,
	formatDisplayId,
	formatFileSize,
	isoOf,
	timeOf,
	useIsYou,
} from './derive';
import { EVAULT_API } from './use-evault-api';
import { useDocumentDrawer } from './use-document-drawer';
import { useRegistryVerify } from './use-registry-verify';
import { useViewCopy } from './use-view-copy';

const RETAINED_COPY_HINT =
	'Transferred away — you kept a copy. Only the current owner can transfer it.';
/* The same rule, a DIFFERENT action — and a hint has to name the action it hangs
   off. Reusing the transfer sentence on Check out is what made SIM-33 read as a
   broken button: the reporter clicked Check out, was told who may TRANSFER the
   document, and concluded the control had failed rather than that it was
   refusing on purpose. */
const RETAINED_COPY_CHECKOUT_HINT =
	'Transferred away — you kept a copy. Only the current owner can check it out.';

/** `source` is a machine key; the Details row is read by a person. */
const SOURCE_LABEL: Record<IVaultSource, string> = {
	envelope: 'Envelope',
	template: 'Template',
	onboarding: 'Onboarding',
};

/**
 * A `Section` that only loads its contents once someone asks for them.
 *
 * `Section` from the kit is always open, which is right for a drawer whose
 * every section is already in hand. Two of these are not: Custody and Integrity
 * both come from `GET /vault-evidence/:id`, which downloads the stored original
 * and re-hashes it. §7.4 is explicit that this happens on expand, not on open —
 * so those two sections need a disclosure, and the disclosure is what fires the
 * fetch.
 *
 * The header is a real `<button>` with `aria-expanded` rather than a div with a
 * click handler: it is the control that decides whether a request is made, and
 * a keyboard user has to be able to reach it.
 */
const LazySection: FC<{
	title: string;
	right?: ReactNode;
	defaultOpen?: boolean;
	onExpand?: () => void;
	children: ReactNode;
}> = ({ title, right, defaultOpen, onExpand, children }) => {
	const [open, setOpen] = useState(!!defaultOpen);

	/* Fires for an opened-by-default section too, so the caller never has to
	   duplicate the first load outside this component. */
	useEffect(() => {
		if (open) onExpand?.();
	}, [open, onExpand]);

	return (
		<div className="dsec">
			<button
				className="sh shx"
				type="button"
				aria-expanded={open}
				onClick={() => setOpen(o => !o)}
			>
				<span className="chev" aria-hidden="true">
					{open ? '▾' : '▸'}
				</span>
				{title}
				{right ? <span className="r2">{right}</span> : null}
			</button>
			{open ? children : null}
		</div>
	);
};

export interface IDocumentDrawerProps {
	api: IEvaultApi;
	/** The row that was clicked. Null renders the drawer closed and empty. */
	document: IVaultDocument | null;
	open: boolean;
	onClose: () => void;
	/** Opens the transfer modal for this one document (§8d). */
	onTransfer: (document: IVaultDocument) => void;
	/** SIM-23 — opens the checkout modal for a document still in the vault. */
	onCheckout: (document: IVaultDocument) => void;
	/** SIM-23 — opens the re-vault modal for one that has been checked out. */
	onRevault: (document: IVaultDocument) => void;
	/** `false` means the server said no; `null` means the probe never answered. */
	blocked?: boolean;
}

/**
 * The document drawer (§7) — the one place the evidence work reaches a person.
 *
 * `GET /vault-evidence` and `GET /vault-audit` have existed since Phase 0 and
 * nothing has ever read them (§2.13): signers, custody, the hash seal and the
 * audit trail were all unreachable from any screen. Everything below the
 * Details section is that, drawn.
 *
 * THE WORDING IS THE FEATURE (R9). A check that could not be RUN is rendered
 * neutral and keeps the server's own sentence explaining which control is
 * missing — never green, never red. `PARTIAL` is amber. The frameworks are
 * listed as *considered*, because which of them applies is the firm's
 * determination and not ours. The conclusion paragraph is the server's
 * `SAFE_CONCLUSION` verbatim, and the drawer never claims more than the
 * response does.
 */
export const DocumentDrawer: FC<IDocumentDrawerProps> = ({
	api,
	document: doc,
	open,
	onClose,
	onTransfer,
	onCheckout,
	onRevault,
	blocked,
}) => {
	const { successNotification, errorNotification } = useNotification();
	const { viewCopy, busyId, ready } = useViewCopy();
	const registryVerify = useRegistryVerify(api);
/*
 * WHO OWNS THIS RECORD, and why the answer is not `isYou(doc.owner?._id)`.
 *
 * `doc.owner` is built by a $lookup from the `users` collection on `ownerId`. When that
 * join misses, the projection returns the same shape it uses for an owner who has left
 * the workspace — `_id: null` — so `isYou` says "not yours" about a record you own, and
 * every control here silently disappears.
 *
 * It misses more often than it looks. A person can carry one id on the platform and a
 * different one in esign's own `users` collection, and the two eVault paths write
 * different ones: DEPOSIT records the platform principal, TRANSFER records the esign
 * user id. On dev2 that left 18 of 40 records with an owner id no `users` row matches,
 * including 11 belonging to the person looking at them.
 *
 * `isRetainedCopy` answers the same question without the join: the server computes it as
 * `ownerId !== viewerId || externalOwner`, comparing the record against the session
 * principal directly. `=== false` therefore means "you are the owner and it has not left
 * the workspace", which is exactly the permission these controls need.
 *
 * STRICT `=== false`, never `!doc.isRetainedCopy`: the field is optional on the type, and
 * a missing one must read as "not yours" rather than opening every control by default.
 *
 * `isYou` stays for DISPLAY, where naming a person is the point and a blank is survivable.
 */
	const isYou = useIsYou();
	const {
		evidence,
		audit,
		certificate,
		loadEvidence,
		ensureEvidence,
		issueCertificate,
		refetchAudit,
	} = useDocumentDrawer(api, open && doc ? doc._id : null);

	/*
	 * Escape closes the drawer — unless a dialog is standing on top of it.
	 *
	 * The transfer modal is opened FROM this drawer and runs its own Escape
	 * handler, so without this check one keypress would close both: the modal
	 * would cancel its pending transfer and the drawer would vanish out from
	 * under the person watching it happen. A live `.sv2-modal` in the document
	 * is the portal root of an open dialog, so "is a dialog open" is a question
	 * with a real answer rather than a prop threaded through three components.
	 */
	useEffect(() => {
		if (!open) return undefined;
		const key = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') return;
			if (window.document.querySelector('.sv2-modal')) return;
			onClose();
		};
		window.document.addEventListener('keydown', key);
		return () => window.document.removeEventListener('keydown', key);
	}, [open, onClose]);

	const reference = doc ? formatDisplayId(doc.displayId) : '';

	const copyReference = useCallback(() => {
		if (!doc) return;
		navigator.clipboard.writeText(formatDisplayId(doc.displayId));
		successNotification(`${formatDisplayId(doc.displayId)} copied`);
	}, [doc, successNotification]);

	/**
	 * The whole response, as it arrived, named by the document's reference.
	 *
	 * Labelled JSON rather than "report" on purpose: the manifest is unsigned
	 * and there is no PDF — calling this an evidence *report* would promise the
	 * signed package §13 describes, which does not exist yet. What it is, is
	 * exactly what the endpoint returned, saved unmodified so it can be diffed
	 * against a later one.
	 */
	const downloadEvidence = useCallback(() => {
		if (!doc || !evidence.data) return;
		const blob = new Blob([JSON.stringify(evidence.data, null, 2)], {
			type: 'application/json',
		});
		const url = URL.createObjectURL(blob);
		const anchor = window.document.createElement('a');
		anchor.href = url;
		anchor.download = `evidence-${doc.displayId}.json`;
		window.document.body.appendChild(anchor);
		anchor.click();
		anchor.remove();
		/* Revoked on the next tick, not immediately: Chrome starts the save
		   asynchronously and a URL revoked in the same frame can cancel it. */
		setTimeout(() => URL.revokeObjectURL(url), 0);
	}, [doc, evidence.data]);

	/**
	 * Issue the certificate of custody, then open the PDF the server stored.
	 *
	 * `window.open` on the server's signed URL, not a blob: the file is already a stored
	 * document with its own short-lived link, and downloading it into the tab only to
	 * hand it back would double the transfer for no gain. Opened in a new tab rather
	 * than navigating, so the drawer — and whatever the reader had expanded in it —
	 * survives.
	 *
	 * The button stays enabled on failure: `certificate.error` is the server's own
	 * sentence, shown inline, and pressing again is a legitimate retry.
	 */
	const downloadCertificate = useCallback(async () => {
		const issued = await issueCertificate();
		if (!issued?.downloadUrl) return;
		window.open(issued.downloadUrl, '_blank', 'noopener,noreferrer');
		successNotification(`${issued.certificateId} issued`);
	}, [issueCertificate, successNotification]);

	/**
	 * A FRESH certificate for a record that already has one. "Download" returns the
	 * existing certificate of record on purpose; this is the owner's explicit way to
	 * capture what has changed since it was issued (a lock placed later, a package
	 * produced, a verifier that now exists). Ordinal N+1; earlier ones stand.
	 */
	const reissueCertificate = useCallback(async () => {
		const issued = await issueCertificate(true);
		if (!issued?.downloadUrl) return;
		window.open(issued.downloadUrl, '_blank', 'noopener,noreferrer');
		successNotification(`${issued.certificateId} re-issued`);
		void refetchAudit();
	}, [issueCertificate, refetchAudit, successNotification]);

	const [exporting, setExporting] = useState(false);

	/**
	 * §12 — the regulatory production package. Two requests: POST builds and stores the
	 * archive and records its hashes in the audit chain, then the archive itself is
	 * fetched with the bearer (the route needs it, and `window.open` carries none) and
	 * saved through a temporary anchor. The package id and hash are shown so the
	 * person exporting can quote them to whoever receives the file.
	 */
	const exportProduction = useCallback(async () => {
		if (!doc || exporting) return;
		setExporting(true);
		try {
			const built = await api.post<{
				_id: string;
				productionId: string;
				packageHash: string;
				fileName: string;
			}>(EVAULT_API.VAULT_PRODUCTIONS, { documentId: doc._id });
			if (built.kind !== 'ok') {
				errorNotification(built.message);
				return;
			}
			const response = await fetch(
				`${API_HOST}${EVAULT_API.VAULT_PRODUCTIONS}/${built.data._id}?action=download`,
				{ headers: { Authorization: `Bearer ${api.vaultToken}` } }
			);
			if (!response.ok) {
				const body = await response.json().catch(() => null);
				errorNotification(
					body?.message ?? 'The production package could not be downloaded.'
				);
				return;
			}
			const blob = await response.blob();
			const url = URL.createObjectURL(blob);
			const anchor = window.document.createElement('a');
			anchor.href = url;
			anchor.download = built.data.fileName || `${built.data.productionId}.zip`;
			window.document.body.appendChild(anchor);
			anchor.click();
			anchor.remove();
			setTimeout(() => URL.revokeObjectURL(url), 0);
			successNotification(
				`${built.data.productionId} exported — package SHA-256 ${built.data.packageHash.slice(0, 16)}…`
			);
			/* The export is audited and §12 now names this package: refresh both. */
			void refetchAudit();
			void loadEvidence();
		} catch {
			errorNotification('The production package could not be exported.');
		} finally {
			setExporting(false);
		}
	}, [
		api,
		doc,
		errorNotification,
		exporting,
		loadEvidence,
		refetchAudit,
		successNotification,
	]);

	/* Empty values are dropped by `KV` itself, so a row is written once here and
	   simply does not appear when the document does not carry it. */
	const detailRows: [string, ReactNode][] = useMemo(() => {
		if (!doc) return [];
		/* Cells are named before the table is built rather than written inline:
		   an array literal holding JSX reads to eslint as a list of children
		   needing keys, which these are not — they are the second half of a
		   label/value pair. */
		const referenceCell = <span className="fund">{reference}</span>;
		const vaultCell = doc.vault ? (
			<Link className="vlink" to={`/evault/${doc.vault._id}`}>
				{doc.vault.name}
			</Link>
		) : (
			''
		);
		const depositedCell = doc.depositedAt ? (
			<span title={isoOf(doc.depositedAt)}>
				{dayOf(doc.depositedAt)} {timeOf(doc.depositedAt)}
			</span>
		) : (
			''
		);

		/* SIM-23 — FIRST, above the reference, because it changes what every row under it
		   means: the vault is describing a record whose original it no longer holds. Only
		   drawn when it is true; a green SIGNED chip on every other document would repeat
		   what the drawer's own header already says. */
		const statusCell =
			doc.status === 'checked_out' ? (
				<StatusChip verdict="ns">CHECKED OUT</StatusChip>
			) : (
				''
			);

		return [
			['Status', statusCell],
			['Reference', referenceCell],
			['Vault', vaultCell],
			['Source', SOURCE_LABEL[doc.source] ?? doc.source],
			['File', doc.fileType ? doc.fileType.toUpperCase() : ''],
			['Pages', doc.pageCount ? String(doc.pageCount) : ''],
			['Size', doc.sizeBytes ? formatFileSize(doc.sizeBytes) : ''],
			['Deposited', depositedCell],
			['Certificate of completion', doc.hasCertificate ? 'Yes' : 'No'],
		];
	}, [doc, reference]);

	const custody = evidence.data?.custody;

	const custodyRows: [string, ReactNode][] = useMemo(() => {
		if (!doc) return [];
		/* An EXTERNAL owner holds the original outside this workspace — it was
		   transferred to a signer with no account here and delivered by email, so
		   there is no id, no profile and nothing in eVault under their name. The
		   chip is the whole point of the row: without it a reader sees a plain
		   name and reasonably assumes a colleague still has the document.
		   `vch ns` is the shared neutral chip (`StatusChip`) — neither a pass nor
		   a failure, which is exactly what this is. */
		const ownerCell = (
			<span title={doc.owner?.email}>
				{canActAsOwner(doc) || isYou(doc.owner?._id)
					? 'You'
					: doc.owner?.name || doc.owner?.email || ''}
				{doc.owner?.external ? (
					<>
						{' '}
						<StatusChip verdict="ns">OUTSIDE WORKSPACE</StatusChip>
					</>
				) : null}
			</span>
		);
		/* THE LEGAL OWNER, beside the person. Read from the record itself
		   (`shapeDocument` projects it), so the row is drawn without waiting for the
		   evidence call — which downloads and re-hashes the stored original, far too
		   much work for one line of text.

		   A record deposited before the entity was required and never transferred
		   since carries none: NOT ESTABLISHED, the same neutral chip controller
		   authentication uses. Nobody was asked — that is neither a blank field nor a
		   failure, and the drawer must not imply either. */
		const entityCell = doc.ownerEntity?.name ? (
			<span>
				{doc.ownerEntity.name}
				{doc.ownerEntity.kind === 'individual' ? (
					<>
						{' '}
						<StatusChip verdict="ns">INDIVIDUAL</StatusChip>
					</>
				) : null}
			</span>
		) : (
			<StatusChip verdict="ns">NOT ESTABLISHED</StatusChip>
		);
		const rows: [string, ReactNode][] = [
			['Owner', ownerCell],
			['Owning entity', entityCell],
		];

		/* SIM-23 — BEFORE the evidence-derived rows, and independent of them.
		   `checkedOut`/`revaultedFrom` are projected onto the row itself, so these appear
		   the instant Custody is expanded rather than waiting on the re-hash that
		   `GET /vault-evidence/:id` performs — and they still appear when that call
		   fails, which for a checked-out record is the state most worth reading. */
		if (doc.checkedOut?.at) {
			const takenCell = (
				<span title={isoOf(doc.checkedOut.at)}>
					{dayOf(doc.checkedOut.at)} {timeOf(doc.checkedOut.at)} ·{' '}
					{isYou(doc.checkedOut.byId) ? 'You' : doc.owner?.name || 'the holder'}
					{doc.checkedOut.reason ? ` — ${doc.checkedOut.reason}` : ''}
				</span>
			);
			rows.push(['Checked out', takenCell]);
			if (doc.checkedOut.revaultedAs) {
				/* The REFERENCE the re-vault stored beside the id, which is what the rest
				   of this panel calls a document. The id stays as the fallback — it is
				   what the record carries on a back-link written before that field
				   existed, and printing it is better than printing nothing. */
				const revaultedCell = doc.checkedOut.revaultedAsDisplayId ? (
					<span>{formatDisplayId(doc.checkedOut.revaultedAsDisplayId)}</span>
				) : (
					<span className="fund">{doc.checkedOut.revaultedAs}</span>
				);
				rows.push(['Re-vaulted as', revaultedCell]);
			}
		}

		if (doc.revaultedFrom?.displayId) {
			/* THREE outcomes, not two. `sealNote` means the retained bytes could not be
			   compared at all — a gap — and rendering that as NO MATCH would put a
			   tampering finding on a record nobody touched, which is the exact failure R9
			   exists to prevent. */
			const fromCell = (
				<span title={doc.revaultedFrom.sealNote ?? undefined}>
					{formatDisplayId(doc.revaultedFrom.displayId)}{' '}
					{doc.revaultedFrom.sealNote ? (
						<StatusChip verdict="ns">SEAL NOT COMPARED</StatusChip>
					) : (
						<StatusChip verdict={doc.revaultedFrom.sealMatched ? 'ok' : 'rej'}>
							{doc.revaultedFrom.sealMatched ? 'SEAL MATCH' : 'SEAL NO MATCH'}
						</StatusChip>
					)}
				</span>
			);
			rows.push(['Re-vaulted from', fromCell]);
		}

		if (!custody) return rows;

		if (custody.previousOwnerId) {
			const previousCell = <span className="fund">{custody.previousOwnerId}</span>;
			rows.push(['Previous owner', previousCell]);
		}
		if (custody.transferredAt) {
			const transferredCell = (
				<span title={isoOf(custody.transferredAt)}>
					{dayOf(custody.transferredAt)} {timeOf(custody.transferredAt)}
				</span>
			);
			rows.push(['Transferred', transferredCell]);
		}
		if (custody.retainedBy?.length) {
			rows.push([
				'Retention copies',
				`${custody.retainedBy.length} previous owner(s) keep a watermarked copy`,
			]);
		}
		/* C1: no step-up gates a transfer, so the server says NOT_ESTABLISHED and
		   the drawer says exactly that — neutral, never a pass. */
		if (custody.controllerAuthentication === 'NOT_ESTABLISHED') {
			const notEstablished = <StatusChip verdict="ns">NOT ESTABLISHED</StatusChip>;
			rows.push(['Controller authentication', notEstablished]);
		} else if (custody.controllerAuthentication) {
			rows.push(['Controller authentication', custody.controllerAuthentication]);
		}
		return rows;
	}, [custody, doc, isYou]);

	/* ─── §10 Retention: legal hold and the account-closure trigger ───────── */

	const retention = evidence.data?.retention;
	const storage = evidence.data?.storage;
	/* §8's key verdict, as the server judged it — the drawer never re-derives it. */
	const cmekCheck = evidence.data?.verification.checks.find(
		c => c.key === 'customerManagedKey'
	);
	const [holdReason, setHoldReason] = useState('');
	const [closureDate, setClosureDate] = useState('');
	const [retentionBusy, setRetentionBusy] = useState(false);

	/* Both go through PATCH /vault-documents/:id, and both refetch the evidence:
	   the §10 rows and the Integrity table are derived from the record, so the
	   drawer must show what the server now says rather than what was clicked. */
	const patchDocument = useCallback(
		async (body: unknown, done: string) => {
			if (!doc) return;
			setRetentionBusy(true);
			const result = await api.patch<IVaultDocument>(
				`${EVAULT_API.VAULT_DOCUMENTS}/${doc._id}`,
				body
			);
			setRetentionBusy(false);
			if (result.kind !== 'ok') {
				errorNotification(result.message);
				return;
			}
			successNotification(done);
			setHoldReason('');
			setClosureDate('');
			void loadEvidence();
			void refetchAudit();
		},
		[api, doc, errorNotification, loadEvidence, refetchAudit, successNotification]
	);

	const placeHold = useCallback(
		() =>
			patchDocument(
				{ legalHold: { active: true, reason: holdReason.trim() } },
				'Legal hold placed'
			),
		[holdReason, patchDocument]
	);
	const releaseHold = useCallback(
		() => patchDocument({ legalHold: { active: false } }, 'Legal hold released'),
		[patchDocument]
	);

	/* ─── §10 Disposition and reclassification ────────────────────────────── */

	const [dispReason, setDispReason] = useState('');
	const [dispRetainUntil, setDispRetainUntil] = useState('');
	/**
	 * A disposition decision. `destroy` is irreversible and asks twice: the reason box,
	 * then a confirm naming the record. Every outcome refreshes evidence and audit,
	 * because §10, §8 and the Integrity table all change with it.
	 */
	const decideDisposition = useCallback(
		async (decision: 'retain' | 'hold' | 'destroy') => {
			if (!doc) return;
			const reason = dispReason.trim();
			if (!reason) {
				errorNotification('A reason is required for every disposition decision.');
				return;
			}
			if (decision === 'retain' && !dispRetainUntil) {
				errorNotification('Choose the new minimum destruction date.');
				return;
			}
			if (
				decision === 'destroy' &&
				/* A native confirm on purpose: destruction is irreversible, and the one dialog
				   a user cannot dismiss by habit is the browser's own. */
				// eslint-disable-next-line no-alert
				!window.confirm(
					`Destroy the original of ${doc.displayId} (${doc.name})?\n\nA certificate of destruction is issued first, then the stored bytes are deleted. The record stays as a tombstone with its hashes and audit trail. This cannot be undone.`
				)
			) {
				return;
			}
			setRetentionBusy(true);
			const result = await api.post<{ status: string | null }>(EVAULT_API.VAULT_DISPOSITIONS, {
				documentId: doc._id,
				decision,
				reason,
				...(decision === 'retain'
					? { retainUntil: new Date(`${dispRetainUntil}T00:00:00Z`).toISOString() }
					: {}),
			});
			setRetentionBusy(false);
			if (result.kind !== 'ok') {
				errorNotification(result.message);
				return;
			}
			successNotification(
				decision === 'destroy'
					? 'Original destroyed; certificate of destruction issued'
					: decision === 'retain'
						? 'Retained by decision; the storage lock was extended'
						: 'Legal hold placed'
			);
			setDispReason('');
			setDispRetainUntil('');
			void loadEvidence();
			void refetchAudit();
		},
		[api, dispReason, dispRetainUntil, doc, errorNotification, loadEvidence, refetchAudit, successNotification]
	);

	/* Reclassification: the catalogue is fetched the first time the control is used. */
	const [catalogue, setCatalogue] = useState<IRetentionSchedule[] | null>(null);
	const [reclassCode, setReclassCode] = useState('');
	const [reclassReason, setReclassReason] = useState('');
	const loadCatalogue = useCallback(async () => {
		if (catalogue) return;
		const r = await api.get<IRetentionCatalogue>(EVAULT_API.VAULT_RETENTION_SCHEDULES);
		if (r.kind === 'ok') setCatalogue(r.data.schedules);
	}, [api, catalogue]);
	/**
	 * What the chosen schedule would do to the STORAGE LOCK, before anything is sent.
	 *
	 * The lock only ever grows: GCS lets a Locked retention be extended and refuses to
	 * shorten it. So the two outcomes are not symmetrical and must not read as if they
	 * were — extending is irreversible, shortening simply does not happen and leaves the
	 * record over-retained. The exact new date is the server's to compute; this says
	 * which of the two is about to occur, which is what the person needs to decide.
	 */
	const lockUntil = evidence.data?.storage?.object?.retention?.retainUntil ?? null;
	const chosen = (catalogue ?? []).find(s => s.code === reclassCode) ?? null;
	/* `retention` is the evidence's summary and is undefined until it loads; without it
	   there is nothing to compare against, so no claim is made. */
	const currentYears = retention?.assigned ? retention.retentionYears : undefined;
	const lockImpact: 'permanent' | 'extends' | 'cannot-shorten' | 'same' | null =
		!chosen || currentYears === undefined
			? null
			: chosen.retentionYears === null
				? 'permanent'
				: currentYears === null
					? 'cannot-shorten'
					: chosen.retentionYears > currentYears
						? 'extends'
						: chosen.retentionYears < currentYears
							? 'cannot-shorten'
							: 'same';

	const reclassify = useCallback(async () => {
		if (!doc || !reclassCode) return;
		const reason = reclassReason.trim();
		if (!reason) {
			errorNotification('A reason is required to reclassify a record.');
			return;
		}
		/* Confirm only what cannot be undone. Extending a locked retention fixes these
		   bytes in place for the new period whatever happens afterwards; shortening does
		   not change the lock at all, so it gets the inline warning and no dialog. */
		if (lockImpact === 'permanent' || lockImpact === 'extends') {
			const line =
				lockImpact === 'permanent'
					? `${chosen?.code} is a PERMANENT schedule. The storage lock on this original will be extended to the platform maximum — 100 years — and a locked retention can never be shortened.`
					: `${chosen?.code} keeps this record longer than its current schedule, so the storage lock will be EXTENDED to match.`;
			if (
				// eslint-disable-next-line no-alert
				!window.confirm(
					`${line}\n\nThis cannot be undone: the bytes stay undeletable until the new date even if the record is refiled later.\n\nReclassify ${doc.displayId} anyway?`
				)
			) {
				return;
			}
		}
		setRetentionBusy(true);
		const result = await api.patch<
			IVaultDocument & {
				retentionReclassified?: {
					warning: string | null;
					lockRemainsUntil: string | null;
					lockExtended?: boolean;
				};
			}
		>(`${EVAULT_API.VAULT_DOCUMENTS}/${doc._id}`, {
			retention: { scheduleCode: reclassCode },
			reason,
		});
		setRetentionBusy(false);
		if (result.kind !== 'ok') {
			errorNotification(result.message);
			return;
		}
		const outcome = result.data.retentionReclassified;
		if (outcome?.warning) errorNotification(outcome.warning);
		else successNotification(`Reclassified to ${reclassCode}`);
		setReclassCode('');
		setReclassReason('');
		void loadEvidence();
		void refetchAudit();
	}, [api, chosen, doc, errorNotification, loadEvidence, lockImpact, reclassCode, reclassReason, refetchAudit, successNotification]);
	const recordClosure = useCallback(
		() =>
			patchDocument(
				{ retentionTriggerAt: new Date(`${closureDate}T00:00:00Z`).toISOString() },
				'Account closure recorded'
			),
		[closureDate, patchDocument]
	);

	const retentionRows: [string, ReactNode][] = useMemo(() => {
		if (!retention) return [];
		if (!retention.assigned) {
			return [
				[
					'Schedule',
					<span key="none" className="fund">
						None assigned — choose a record category on the vault
					</span>,
				],
			];
		}
		const at = (iso: string | null) =>
			iso ? (
				<span title={isoOf(iso)}>
					{dayOf(iso)} {timeOf(iso)}
				</span>
			) : (
				''
			);
		const rows: [string, ReactNode][] = [
			['Record category', retention.category ?? ''],
			[
				'Governing schedule',
				<span key="code" className="fund">
					{retention.scheduleCode} · v{retention.policyVersion}
				</span>,
			],
			['Applicable rule', retention.governingRule ?? ''],
			[
				'Retention period',
				`${retention.retentionPeriod ?? ''}${
					retention.accessibleYears !== null
						? ` · first ${retention.accessibleYears} years easily accessible`
						: ''
				}`,
			],
			[
				'Retention trigger',
				retention.triggerAt ? (
					<span>
						{retention.triggerLabel}, {at(retention.triggerAt)}
					</span>
				) : retention.trigger === 'permanent' ? (
					'None — permanent record'
				) : (
					<span>{retention.triggerLabel} — not yet recorded</span>
				),
			],
			[
				'Destroy not before',
				retention.minimumDestructionDate
					? at(retention.minimumDestructionDate)
					: retention.trigger === 'permanent'
						? 'Never'
						: 'Undefined until the trigger is recorded',
			],
			['Disposition authority', retention.dispositionAuthority ?? ''],
			/* §8, beside §10: the storage layer's own lock on this record's object. The
			   two are different facts (a schedule is a rule; a lock is enforcement) and
			   the drawer shows both so a reader can see they agree — or that they do
			   not, which is exactly the case worth seeing. */
			[
				'Storage lock',
				storage?.object?.retention?.mode === 'Locked' ? (
					<span>
						<StatusChip verdict="ok">LOCKED</StatusChip> until{' '}
						{at(storage.object.retention.retainUntil)}
					</span>
				) : storage?.source === 'envelope-document' ? (
					<span className="fund">Not migrated to the originals bucket</span>
				) : storage?.object?.retention ? (
					<span>
						<StatusChip verdict="ns">UNLOCKED</StatusChip> until{' '}
						{at(storage.object.retention.retainUntil)}
					</span>
				) : (
					<span className="fund">None</span>
				),
			],
			/* §8's other control: whose key encrypts this object. Read from the object,
			   not the bucket — a bucket default covers only what is written after it. The
			   verdict is the check's own, so the drawer and the certificate agree. */
			[
				'Encryption key',
				(() => {
					const enc = storage?.encryption;
					const verdict = cmekCheck;
					if (!enc || !verdict) return <span className="fund">Not observed</span>;
					const key = enc.kmsKeyName ?? enc.designatedKmsKeyName ?? enc.bucketDefaultKmsKeyName;
					const short = key ? key.split('/').slice(-1)[0] : null;
					return (
						<span title={verdict.detail ?? undefined}>
							<StatusChip
								verdict={
									verdict.result === 'PASS' ? 'ok' : verdict.result === 'FAIL' ? 'rej' : 'ns'
								}
							>
								{verdict.result === 'PASS'
									? 'PLATFORM-HELD'
									: verdict.result === 'FAIL'
										? 'MISMATCH'
										: 'PROVIDER-HELD'}
							</StatusChip>{' '}
							{verdict.result === 'PASS' && short
								? `Cloud KMS · ${short}`
								: enc.atRest === 'google-managed'
									? 'Google-managed keys'
									: enc.atRestLabel ?? ''}
						</span>
					);
				})(),
			],
			[
				'Legal hold',
				retention.legalHold?.active ? (
					<span>
						<StatusChip verdict="ok">ACTIVE</StatusChip>{' '}
						{retention.legalHold.storageHold ? '' : '(not mirrored to storage) '}
						{retention.legalHold.reason}
					</span>
				) : retention.legalHold ? (
					<span>Released {at(retention.legalHold.releasedAt)}</span>
				) : (
					'None'
				),
			],
		];
		return rows;
	}, [retention, storage, cmekCheck]);

	/* ─── Ledger anchor (phase 1 Task 6) ────────────────────────────
	   Read straight off `doc.registry` — the list projection already carries
	   it, so unlike Custody/Retention this needs no evidence fetch. Once a
	   live "Verify on chain" has run, `useRegistryVerify`'s own per-document
	   result stands in for the *Last verify* row: the drawer shows what the
	   click just found rather than the snapshot the list opened with. */
	const registry = doc?.registry ?? null;
	const ledger = registry?.ledger ?? null;
	const liveVerify = doc ? registryVerify.result[doc._id] : undefined;

	const registryRows: [string, ReactNode][] = useMemo(() => {
		if (!doc) return [];
		if (!ledger?.documentId) {
			return [
				[
					'Ledger status',
					registry?.reason ? `not registered: ${registry.reason}` : 'not registered',
				],
			];
		}
		const at = (iso: string | null) =>
			iso ? (
				<span title={isoOf(iso)}>
					{dayOf(iso)} {timeOf(iso)}
				</span>
			) : (
				''
			);
		const recordOwnerId = doc.owner?._id ?? null;
		const ledgerOwnerId = ledger.ownerRef;
		const ownersDiffer =
			!!ledgerOwnerId && !!recordOwnerId && ledgerOwnerId !== recordOwnerId;
		/* A fresh result carrying `error` is "could not verify", not a new
		   answer — the stored `lastVerify` from the list row is still the last
		   real one. */
		const verify = liveVerify && !liveVerify.error ? liveVerify : registry?.lastVerify;

		/* Hoisted out of the rows array, the way `matchChip` and `timestampCell`
		   above are: JSX written inline inside an array literal is what
		   react/jsx-key warns about, and `KV` keys its own rows anyway. */
		const recordOwnerCell = (
			<span>
				<span className="fund">{recordOwnerId ?? ''}</span>
				{ownersDiffer ? (
					<>
						{' '}
						<StatusChip verdict="rev">DIFFERS</StatusChip>
					</>
				) : null}
			</span>
		);
		const lastVerifyCell = verify?.at ? (
			<span title={isoOf(verify.at)}>
				{dayOf(verify.at)} {timeOf(verify.at)}{' '}
				<StatusChip verdict={verify.matches ? 'ok' : 'rej'}>
					{verify.matches ? 'MATCHES' : 'DOES NOT MATCH'}
				</StatusChip>
			</span>
		) : (
			''
		);

		/* Attestations: n of m ledger entries against the document's own signers.
		   `doc.signers` is built at deposit from EVERY recipient with an email, not
		   only those who sign (`evault-deposit.ts`), so the denominator filters to the
		   signing action. A document deposited before `action` was stored carries none
		   at all, and counting every recipient there would print "1 of 4 PENDING" over a
		   ledger record that is complete — so the row is dropped instead: `m === 0`
		   leaves an empty cell, which `KV` omits.
		   'Needs to sign' is the literal value of esign.api's
		   `RECIPIENT_ACTIONS.NeedsToSign` (`services/envelopes/types`), which is what the
		   deposit writes into this field. */
		const signers = doc.signers ?? [];
		const m = signers.some(s => typeof s.action === 'string')
			? signers.filter(s => s.action === 'Needs to sign').length
			: 0;
		const n = (ledger.attestations ?? []).length;
		const attestationsCell =
			m > 0 ? (
				<span>
					{n} of {m}{' '}
					<StatusChip verdict={n === m ? 'ok' : 'wait'}>
						{n === m ? 'COMPLETE' : 'PENDING'}
					</StatusChip>
				</span>
			) : (
				''
			);

		const ownerChange = registry?.ownerChange ?? null;
		const ownerChangeCell = ownerChange ? (
			ownerChange.status === 'changed' ? (
				<StatusChip verdict="ok">RECORDED</StatusChip>
			) : ownerChange.status === 'pending' ? (
				<span>
					<StatusChip verdict="wait">PENDING</StatusChip>
					{ownerChange.reason ? ` — ${ownerChange.reason}` : ''}
				</span>
			) : (
				<span>
					<StatusChip verdict="rev">REJECTED</StatusChip>
					{ownerChange.reason ? ` — ${ownerChange.reason}` : ''}
				</span>
			)
		) : (
			''
		);

		/* An empty cell is dropped by `KV`, so an unanswered row is absent
		   rather than dashed. */
		return [
			[
				'Ledger status',
				ledger.status ? ledger.status.charAt(0).toUpperCase() + ledger.status.slice(1) : '',
			],
			['Registered', at(ledger.registeredAt)],
			['Attestations', attestationsCell],
			['Ledger owner', ledgerOwnerId ? <span className="fund">{ledgerOwnerId}</span> : ''],
			['Owner change', ownerChangeCell],
			/* An owner-less record (an external holder with no member ref) drops the
			   row like every other unanswered one, instead of a label over a blank. */
			['Record owner', recordOwnerId ? recordOwnerCell : ''],
			['Last verify', lastVerifyCell],
		];
	}, [doc, ledger, registry, liveVerify]);

	/* The sentence under the Verify on chain button — a FRESH result only,
	   not the stored `lastVerify` KV row above, which stays put until the
	   button is pressed. An `error` means the ledger could not answer THIS
	   click, a different fact from "the bytes don't match" with its own
	   wording. */
	const verifyResultLine = useMemo(() => {
		if (!doc) return null;
		const r = registryVerify.result[doc._id];
		if (!r) return null;
		if (r.error) return `Could not verify — ${r.error}`;
		/* No `registeredAt` on an adopted entry (a 409 the ledger already held), and
		   "recorded on  " with an empty date reads as a rendering bug rather than a
		   missing field. The verdict is the point; the date is a detail. */
		const when = `${dayOf(ledger?.registeredAt ?? null)} ${timeOf(
			ledger?.registeredAt ?? null
		)}`.trim();
		const tail = ledger?.registeredAt ? ` recorded on ${when}` : '';
		return r.matches
			? `Matches the ledger entry${tail}`
			: `Does not match the ledger entry${tail}`;
	}, [doc, registryVerify.result, ledger]);

	/* The certificate's own anchor mark, next to the sealed/unsealed sentence.
	   Checked in order: a recorded ledger id is ANCHORED, a live job is ANCHOR
	   PENDING, anything else shows nothing. `reason` is NOT a suppressor — an
	   adoption and an accepted-with-no-id both write one ALONGSIDE a real ledger
	   id or a queued job, and suppressing on it printed no chip on certificates
	   that are anchored. */
	const certificateAnchorChip = useMemo(() => {
		const certRegistry = certificate.data?.registry;
		if (!certRegistry) return null;
		if (certRegistry.ledger.documentId) return <StatusChip verdict="ok">ANCHORED</StatusChip>;
		if (certRegistry.job.status === 'queued' || certRegistry.job.status === 'running') {
			return <StatusChip verdict="wait">ANCHOR PENDING</StatusChip>;
		}
		return null;
	}, [certificate.data]);

	if (!doc) return <Drawer open={false} label="Document details" />;

	/* One band and one retry for both sections, because it is one request.

	   TWO DIFFERENT FAILURES, TWO SENTENCES. A status means the server answered and
	   refused — its own sentence is the finding, and "did not respond" over it
	   sent a reader to check their network for what was a permission answer (the
	   retained-copy 404, dev2, 23 Sep 2026). No status means nothing came back,
	   and only then is it a connection problem. */
	const evidenceProblem = evidence.error ? (
		<div className="errbar" title={evidence.error}>
			<span className="et">
				{evidence.errorStatus
					? `Could not load the evidence package — the eVault service answered ${evidence.errorStatus}: ${evidence.error}`
					: 'Could not load the evidence package. The eVault service did not respond — this is a connection problem, not missing evidence.'}
			</span>
			<button
				className="reinv errbar__act"
				type="button"
				onClick={() => void loadEvidence()}
			>
				Try again
			</button>
		</div>
	) : null;

	const verification = evidence.data?.verification;
	const overall = verification ? OVERALL_CHIP[verification.overall] : null;
	const hashes = evidence.data?.hashes;
	const manifest = evidence.data?.manifest;
	const frameworks = evidence.data?.report?.frameworksConsidered ?? [];
	/*
	 * Named rather than written into the `KV` rows literal — see `detailRows`.
	 *
	 * THREE OUTCOMES, NOT TWO. `hashes.matches` is a plain boolean and it is
	 * `false` both when the two digests differ and when there is nothing to
	 * compare — a seeded row, or an original the storage layer could not hand
	 * back. Rendering that second case as a red NO MATCH would assert a
	 * tampering finding nobody measured, which is the exact failure R9 exists to
	 * prevent; the checks table above already says NOT AVAILABLE, with the
	 * reason, and this row says the same rather than contradicting it.
	 */
	/*
	 * The RFC 3161 token over the seal.
	 *
	 * Three states, not two. A token that is HELD but does not verify is the one this
	 * row exists for, and collapsing it into "no timestamp" would hide the only case
	 * worth looking at. `verified === null` means none is held — a gap, shown neutral
	 * and never as a failure: most documents predate timestamping and none of them
	 * were tampered with.
	 */
	const stamp = evidence.data?.timestamp;
	const timestampCell =
		!stamp || stamp.status !== 'obtained' ? (
			<span title={stamp?.reason ?? undefined}>
				<StatusChip verdict="ns">NOT AVAILABLE</StatusChip>
			</span>
		) : (
			<span title={[stamp.tsa, stamp.reason].filter(Boolean).join(' — ')}>
				<StatusChip
					verdict={stamp.verified === false ? 'rej' : stamp.verified ? 'ok' : 'ns'}
				>
					{stamp.verified === false
						? 'DOES NOT VERIFY'
						: stamp.verified
							? 'VERIFIED'
							: 'NOT CHECKED'}
				</StatusChip>{' '}
				<span className="fund" title={isoOf(stamp.genTime ?? '')}>
					{dayOf(stamp.genTime ?? '')} {timeOf(stamp.genTime ?? '')}
				</span>
			</span>
		);

	/* Read from the ROW, not from the evidence package: the footer has to be right before
	   anybody expands Custody, and for a checked-out record the evidence call is the one
	   most likely not to have been made. */
	const checkedOut = doc.status === 'checked_out';
	const legalHeld = !!doc.legalHold?.active;

	const comparable = !!(hashes?.sealedAtDeposit && hashes?.recomputedNow);
	const matchChip = !hashes ? null : !comparable ? (
		<StatusChip verdict="ns">NOT AVAILABLE</StatusChip>
	) : (
		<StatusChip verdict={hashes.matches ? 'ok' : 'rej'}>
			{hashes.matches ? 'MATCH' : 'NO MATCH'}
		</StatusChip>
	);

	return (
		<Drawer open={open} label="Document details">
			<DrawerHead
				title={doc.name}
				chip={
					/* A checked-out record is neither a pass nor a failure — it is a custody
					   state — so the header chip is neutral rather than the green one every
					   other record gets; a tombstone is the one red state. */
					<StatusChip
						verdict={
							doc.status === 'checked_out' ? 'ns' : doc.status === 'destroyed' ? 'rej' : 'ok'
						}
					>
						{doc.status.replace('_', ' ').toUpperCase()}
					</StatusChip>
				}
				onClose={onClose}
			>
				<div className="dpad dref">
					<CopyRow
						label="Reference"
						value={reference}
						onCopy={() => successNotification(`${reference} copied`)}
					/>
				</div>
			</DrawerHead>

			<Section title="Details">
				<KV rows={detailRows} />
				{doc.envelopeId ? (
					<div className="dpad">
						<CopyRow
							label="Envelope"
							value={String(doc.envelopeId)}
							onCopy={() => successNotification('Envelope id copied')}
						/>
					</div>
				) : null}
			</Section>

			<Section
				title="Signers"
				right={`${doc.signers?.length ?? 0} signer${
					(doc.signers?.length ?? 0) === 1 ? '' : 's'
				}`}
			>
				{doc.signers?.length ? (
					doc.signers.map(signer => (
						<div className="sgn" key={signer._id ?? signer.email}>
							<span className="nm">{signer.name || signer.email}</span>
							<span className="mail">{signer.email}</span>
						</div>
					))
				) : (
					<div className="sv2-empty">No signers recorded for this document.</div>
				)}
			</Section>

			<LazySection title="Custody" onExpand={ensureEvidence}>
				{evidenceProblem}
				<KV rows={custodyRows} />
				{evidence.isLoading && !evidence.isLoaded && (
					<div className="sv2-loading">Loading custody…</div>
				)}
			</LazySection>

			{/* §10 — what the record is filed under, and the two acts a controller can
			    perform on it. Both are audited into the hash chain server-side; the
			    drawer offers them only to the current owner, mirroring the server's own
			    check, so a retained-copy holder never sees a button that would 403. */}
			<LazySection
				title="Retention"
				onExpand={ensureEvidence}
				right={
					retention ? (
						<StatusChip
							verdict={
								(CHECK_CHIP[retention.verdict.result] ?? CHECK_CHIP.NOT_AVAILABLE)
									.cls
							}
						>
							{
								(CHECK_CHIP[retention.verdict.result] ?? CHECK_CHIP.NOT_AVAILABLE)
									.label
							}
						</StatusChip>
					) : undefined
				}
			>
				{evidenceProblem}
				{evidence.isLoading && !evidence.isLoaded && (
					<div className="sv2-loading">Loading retention…</div>
				)}
				{retention && (
					<>
						<KV rows={retentionRows} />
						<div className="dpad">
							<div className="foot frm">{retention.verdict.detail}</div>
						</div>
						{evidence.data?.disposition ? (
							<div className="dpad">
								<div className={`foot ${evidence.data.disposition.status === 'destroyed' ? 'frm' : ''}`}>
									{evidence.data.disposition.status === 'destroyed'
										? `Original DESTROYED ${dayOf(evidence.data.disposition.destroyedAt ?? evidence.data.disposition.decidedAt)} under a disposition decision: ${evidence.data.disposition.reason}. Certificate of destruction ${evidence.data.disposition.certificateId ?? '—'}. This record is a tombstone: its hashes and audit trail are kept; the bytes are gone.`
										: `Retained by decision ${dayOf(evidence.data.disposition.decidedAt)}: ${evidence.data.disposition.reason}. Destroy not before ${dayOf(evidence.data.disposition.newDestructionDate ?? '')}.`}
								</div>
							</div>
						) : null}
						{canActAsOwner(doc) &&
						retention.assigned &&
						doc.status !== 'destroyed' &&
						doc.status !== 'checked_out' &&
						retention.verdict.result === 'ELIGIBLE_FOR_DISPOSITION' ? (
							<div className="dpad">
								<div className="fld">
									{/* "Disposition due", not "due — <title>". The person reading this is the
									    owner and is the one who decides; heading the box with a role they may
									    not hold reads as "wait for someone else". The schedule's title belongs
									    in the sentence below as the firm's policy, which is what it is. */}
									<div className="flab">Disposition due</div>
									<input
										value={dispReason}
										placeholder="Reason for the decision (required)"
										maxLength={240}
										disabled={retentionBusy}
										onChange={e => setDispReason(e.target.value)}
									/>
									<div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
										<input
											type="date"
											value={dispRetainUntil}
											min={new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)}
											disabled={retentionBusy}
											onChange={e => setDispRetainUntil(e.target.value)}
										/>
										<button className="dbtn" type="button" disabled={retentionBusy} onClick={() => void decideDisposition('retain')}>
											Retain until date
										</button>
										<button className="dbtn" type="button" disabled={retentionBusy} onClick={() => void decideDisposition('hold')}>
											Place hold
										</button>
										{/* Always offered to the owner. There was a workspace switch in front of
										    this, guarding a disposition-authority title the service writes into its
										    own schedule and never verifies; the owner is the accountable party and
										    could already transfer or download the original. What still stops a
										    destruction is real: the schedule, a legal hold, the storage lock, and
										    the certificate that has to be issued first. */}
										<button className="dbtn" type="button" disabled={retentionBusy} onClick={() => void decideDisposition('destroy')}>
											Destroy original
										</button>
									</div>
									<div className="fhint">
										The retention period has elapsed. As this record&apos;s owner, the decision is
										yours{retention.dispositionAuthority ? `; the schedule makes the ${retention.dispositionAuthority} responsible for it under your firm's own policy` : ''}.
										Each decision is audited; destruction issues a certificate of destruction first and leaves the record as a tombstone.
									</div>
								</div>
							</div>
						) : null}
						{canActAsOwner(doc) && retention.assigned && doc.status !== 'destroyed' && doc.status !== 'checked_out' ? (
							<div className="dpad">
								<div className="fld">
									<div className="flab">Reclassify</div>
									<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
										<select
											value={reclassCode}
											disabled={retentionBusy}
											onFocus={() => void loadCatalogue()}
											onChange={e => setReclassCode(e.target.value)}
										>
											<option value="">Move to another schedule…</option>
											{(catalogue ?? []).filter(s => s.code !== retention.scheduleCode).map(s => (
												<option key={s.code} value={s.code}>
													{s.code} · {s.category} · {s.retentionYears === null ? 'permanent' : `${s.retentionYears}y`}
												</option>
											))}
										</select>
										<input
											value={reclassReason}
											placeholder="Reason (required)"
											maxLength={240}
											disabled={retentionBusy}
											onChange={e => setReclassReason(e.target.value)}
										/>
										<button className="dbtn" type="button" disabled={retentionBusy || !reclassCode} onClick={() => void reclassify()}>
											Reclassify
										</button>
									</div>
									{lockImpact && lockImpact !== 'same' ? (
										<div className="foot frm" style={{ marginTop: 6 }}>
											{lockImpact === 'permanent'
												? `⚠ ${chosen?.code} is permanent: the storage lock will be extended to the platform maximum of 100 years. This cannot be undone.`
												: lockImpact === 'extends'
													? `⚠ ${chosen?.code} is longer than the current schedule: the storage lock will be EXTENDED to match, and a locked retention can never be shortened afterwards.`
													: `⚠ ${chosen?.code} is shorter than the current schedule, but the storage lock cannot be shortened${
															lockUntil ? `: these bytes stay undeletable until ${dayOf(lockUntil)}` : ''
														}. The record will be reported as over-retained on its certificate.`}
										</div>
									) : null}
									<div className="fhint">
										The previous schedule stays in the record&apos;s history. A storage lock can only be extended: moving to a shorter schedule does not shorten it, and the certificate will say so.
									</div>
								</div>
							</div>
						) : null}
												{/* Not on a checked-out record. Every act here is a records-management
						    decision about a record the vault is holding, and it is not holding
						    this one — `PATCH /vault-documents/:id` refuses them with a 409, so
						    offering the controls would be offering a toast. */}
						{canActAsOwner(doc) && retention.assigned && doc.status !== 'checked_out' ? (
							<div className="dpad">
								{retention.trigger === 'account_closure' && !retention.triggerAt ? (
									<div className="fld">
										<div className="flab">Record account closure</div>
										<div style={{ display: 'flex', gap: 8 }}>
											<input
												type="date"
												value={closureDate}
												max={new Date().toISOString().slice(0, 10)}
												disabled={retentionBusy}
												onChange={e => setClosureDate(e.target.value)}
											/>
											<button
												className="dbtn"
												type="button"
												disabled={retentionBusy || !closureDate}
												onClick={() => void recordClosure()}
											>
												Record
											</button>
										</div>
										<div className="fhint">
											Starts the {retention.retentionPeriod} retention clock. The date
											is audited and cannot be in the future.
										</div>
									</div>
								) : null}
								{retention.legalHold?.active ? (
									<div className="dbtns">
										<button
											className="dbtn"
											type="button"
											disabled={retentionBusy}
											onClick={() => void releaseHold()}
										>
											{retentionBusy ? 'Working…' : 'Release legal hold'}
										</button>
									</div>
								) : (
									<div className="fld">
										<div className="flab">Place legal hold</div>
										<div style={{ display: 'flex', gap: 8 }}>
											<input
												value={holdReason}
												placeholder="Reason, e.g. litigation matter or regulatory request"
												maxLength={240}
												disabled={retentionBusy}
												onChange={e => setHoldReason(e.target.value)}
											/>
											<button
												className="dbtn"
												type="button"
												disabled={retentionBusy || !holdReason.trim()}
												onClick={() => void placeHold()}
											>
												{retentionBusy ? 'Working…' : 'Place hold'}
											</button>
										</div>
										<div className="fhint">
											Suspends disposition regardless of the schedule, and sets a
											storage-layer hold on the stored original.
										</div>
									</div>
								)}
							</div>
						) : null}
					</>
				)}
			</LazySection>

			<LazySection
				title="Integrity"
				onExpand={ensureEvidence}
				right={
					overall ? (
						<StatusChip verdict={overall.cls}>{overall.label}</StatusChip>
					) : undefined
				}
			>
				{evidenceProblem}

				{evidence.isLoading && !evidence.isLoaded && (
					<div className="sv2-loading">Loading evidence…</div>
				)}

				{/* The ledger anchor — OUTSIDE the evidence gate below, on purpose. It is read
				    straight off the row's own `registry` (the list projection carries it), so
				    it needs no fetch and must not vanish when the evidence call is slow or
				    fails: "not registered" is a fact about this record whether or not the
				    checks table ever arrives. The two copyable values follow the hashes
				    block's rule: a value the reader can check against the ledger, or no row
				    at all. */}
				<KV rows={registryRows} />
				{ledger && (ledger.documentId || ledger.contentHash) && (
					<div className="dpad">
						{ledger.documentId && (
							<div className="hash">
								<CopyRow
									label="Registry document id"
									value={ledger.documentId}
									onCopy={() => successNotification('Registry document id copied')}
								/>
							</div>
						)}
						{ledger.contentHash && (
							<div className="hash">
								<CopyRow
									label="Content hash"
									value={ledger.contentHash}
									onCopy={() => successNotification('Content hash copied')}
								/>
							</div>
						)}
					</div>
				)}
				<div className="dpad">
					<div className="dbtns">
						{/* The live re-read, on a click only: it costs a call out to the ledger
						    and writes `registry.lastVerify` server-side. Disabled until there is a
						    ledger entry to check against. */}
						<button
							className="dbtn"
							type="button"
							disabled={
								!doc.registry?.ledger?.documentId ||
								registryVerify.busyId === doc._id
							}
							onClick={() => void registryVerify.verify(doc._id)}
						>
							{registryVerify.busyId === doc._id ? 'Verifying…' : 'Verify on chain'}
						</button>
					</div>
					{verifyResultLine ? (
						<div className="foot frm">{verifyResultLine}</div>
					) : null}
				</div>

				{verification && (
					<>
						<div className="tscroll">
							<table className="stbl ichk">
								<thead>
									<tr>
										<th>Check</th>
										<th className="rescol">Result</th>
									</tr>
								</thead>
								<tbody>
									{verification.checks.map(one => {
										const chip = CHECK_CHIP[one.result] ?? CHECK_CHIP.NOT_AVAILABLE;
										return (
											<tr key={one.key}>
												<td>
													<div className="nm">{one.label}</div>
													{/* The sentence is the point: it names the report
													    section the missing control belongs to, which is
													    the difference between "not available" and an
													    unexplained blank. */}
													{one.detail ? <div className="cdet">{one.detail}</div> : null}
												</td>
												<td className="rescol">
													<StatusChip verdict={chip.cls}>{chip.label}</StatusChip>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>

						{hashes && (
							<>
								<KV
									rows={[
										['Algorithm', hashes.algorithm],
										['Match', matchChip],
										['Trusted timestamp', timestampCell],
									]}
								/>
								{/* Truncated for reading, whole for copying: the value the
								    copy button writes is the 64-character digest that can be
								    checked against `shasum -a 256`. A digest the server could
								    not produce gets no row at all — a copy button that writes
								    an em dash is worse than an absent one, and the checks
								    table above already says which control was missing. */}
								{(hashes.sealedAtDeposit || hashes.recomputedNow) && (
									<div className="dpad">
										{hashes.sealedAtDeposit && (
											<div className="hash">
												<CopyRow
													label="Sealed at deposit"
													value={hashes.sealedAtDeposit}
													onCopy={() => successNotification('Seal hash copied')}
												/>
											</div>
										)}
										{hashes.recomputedNow && (
											<div className="hash">
												<CopyRow
													label="Recomputed now"
													value={hashes.recomputedNow}
													onCopy={() => successNotification('Recomputed hash copied')}
												/>
											</div>
										)}
									</div>
								)}
							</>
						)}

						<div className="dpad">
							{/* Verbatim. The server owns this sentence precisely so a UI
							    cannot soften it into a claim of compliance. */}
							<div className="foot concl">{verification.conclusion}</div>
							{frameworks.length > 0 && (
								<div className="foot frm">
									Frameworks considered: {frameworks.join(' · ')}
								</div>
							)}
							{manifest && (
								<div className="foot frm">
									{manifest.signed
										? 'Manifest: signed'
										: `Manifest: not signed — ${manifest.signatureGap}`}
								</div>
							)}
							<div className="dbtns">
								<button
									className="dbtn"
									type="button"
									disabled={!evidence.data}
									onClick={downloadEvidence}
								>
									Download evidence (JSON)
								</button>
								{/* The SEALED artefact, next to the unsigned one. The label says
								    PDF because the two buttons produce different things and the
								    difference matters: the JSON is this endpoint's response, the
								    PDF is the signed certificate of custody. */}
								{/* Owner only, like the endpoint: `POST /vault-certificates` issues
								    evidence about an original and refuses anyone who does not
								    control it. A retainer got the same button and a 404 for pressing
								    it; the certificate for their transfer went to the new owner with
								    the notice, and the copy they hold says so. */}
								<button
									className="dbtn"
									type="button"
									disabled={certificate.isLoading || !canActAsOwner(doc)}
									title={
										canActAsOwner(doc)
											? undefined
											: 'Only the current owner can issue a certificate. The one issued at transfer went to them with the notice.'
									}
									onClick={() => void downloadCertificate()}
								>
									{certificate.isLoading
										? 'Issuing certificate…'
										: 'Download certificate (PDF)'}
								</button>
								{canActAsOwner(doc) ? (
									<button
										className="dbtn"
										type="button"
										disabled={certificate.isLoading}
										title="Issue a fresh certificate (a new ordinal) reflecting the record as it is now; the existing one is kept"
										onClick={() => void reissueCertificate()}
									>
										Re-issue certificate
									</button>
								) : null}
								{/* §12: everything an examiner needs, in one archive. Owner-only,
								    because the package is the record leaving the system. */}
								{canActAsOwner(doc) && doc.status !== 'destroyed' ? (
									<button
										className="dbtn"
										type="button"
										disabled={exporting}
										title="Original, certificate, sealed manifest and signature, timestamp tokens, audit events, retention record, SHA256SUMS"
										onClick={() => void exportProduction()}
									>
										{exporting ? 'Building package…' : 'Export production package (zip)'}
									</button>
								) : null}
							</div>
							{certificate.error ? (
								<div className="foot frm" title={certificate.error}>
									The certificate could not be issued. {certificate.error}
								</div>
							) : null}
							{/* Only after an issue in this session — the drawer does not fetch the
							    certificate list on open, so saying nothing is the honest state
							    rather than "no certificate exists". */}
							{certificate.data ? (
								<div className="foot frm">
									Certificate {certificate.data.certificateId} issued{' '}
									{dayOf(certificate.data.issuedAt)} {timeOf(certificate.data.issuedAt)} —{' '}
									{certificate.data.sealed
										? 'sealed'
										: `UNSEALED: ${certificate.data.sealReason ?? 'no signing key is configured on the server'}`}
									{/* §14(a): the second opinion, stated the way the certificate states it. */}
									{certificate.data.attested
										? certificate.data.attestationAgrees
											? ' · independently verified, agrees'
											: ' · independently verified, DISAGREES (see Section 14)'
										: ` · independent verification not performed${
												certificate.data.attestationReason
													? `: ${certificate.data.attestationReason}`
													: ''
											}`}
									{certificateAnchorChip ? <> {certificateAnchorChip}</> : null}
								</div>
							) : null}
						</div>
					</>
				)}
			</LazySection>

			<Section
				title="Activity"
				right={audit.data ? `${audit.data.length} events` : undefined}
			>
				{audit.error && (
					<div className="errbar" title={audit.error}>
						<span className="et">
							{audit.errorStatus
								? `Could not load this document's activity — the eVault service answered ${audit.errorStatus}: ${audit.error}`
								: "Could not load this document's activity. The eVault service did not respond — this is a connection problem, not an empty trail."}
						</span>
						<button
							className="reinv errbar__act"
							type="button"
							onClick={() => void refetchAudit()}
						>
							Try again
						</button>
					</div>
				)}

				{audit.isLoading && !audit.isLoaded && (
					<div className="sv2-loading">Loading activity…</div>
				)}

				{audit.isLoaded && !audit.error && !audit.data?.length && (
					<div className="sv2-empty">No events recorded for this document.</div>
				)}

				{!!audit.data?.length && (
					<div className="tscroll">
						<table className="stbl atbl">
							<thead>
								<tr>
									<th>Time</th>
									<th>User</th>
									<th>Action</th>
									<th>Activity</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								{audit.data.map(row => (
									<tr
										key={row.id}
										/* Signing rows come from the envelope's own log and sit
										   outside the eVault chain. An eVault row written before
										   the per-workspace hash chain existed carries `seq: null`
										   — shown like any other, with the title saying why. The
										   IP lives here too: it is evidence, not something to read
										   in every row. */
										title={
											row.source === 'esign'
												? 'from the eSign signing log'
												: [
														row.seq === null
															? 'recorded before the audit chain was enabled'
															: `seq ${row.seq} · ${row.eventHash ?? ''}`,
														row.ip ? `IP ${row.ip}` : '',
												  ]
														.filter(Boolean)
														.join(' · ')
										}
									>
										<td className="dep" title={isoOf(row.time)}>
											{dayOf(row.time)}
											<span className="fund">{timeOf(row.time)}</span>
										</td>
										<td className="usr" title={row.user.email}>
											<span className="nm">
												{isYou(row.user.id) ? 'You' : row.user.name}
											</span>
											{row.user.email && row.user.email !== row.user.name ? (
												<span className="mail">{row.user.email}</span>
											) : null}
										</td>
										<td className="act">{row.action}</td>
										<td className="actv">{row.activity}</td>
										<td>
											<StatusChip verdict={row.tone}>{row.status.toUpperCase()}</StatusChip>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</Section>

			<div className="dfoot">
				<div className="dbtns">
					{/* A CHECKED-OUT record has one act left on it, so it gets one primary
					    button rather than the ordinary ones greyed out. Transfer is hidden
					    below for the same reason: the server refuses it. */}
					{doc.status === 'destroyed' ? null : checkedOut ? (
						<button
							className="dbtn pri"
							type="button"
							disabled={blocked}
							title={
								blocked
									? 'Checkouts need the eVault add-on.'
									: 'Deposits the retained sealed bytes as a new linked record'
							}
							onClick={() => onRevault(doc)}
						>
							Re-vault
						</button>
					) : (
						<>
							<button
								className="dbtn pri"
								type="button"
								disabled={blocked || !!doc.isRetainedCopy}
								title={
									blocked
										? 'Transfers need the eVault add-on.'
										: doc.isRetainedCopy
											? RETAINED_COPY_HINT
											: undefined
								}
								onClick={() => onTransfer(doc)}
							>
								Transfer ownership
							</button>
							{/* Beside Transfer, because they are the two ways an original
							    leaves — one changes who owns it, the other ends the vault's
							    custody of it altogether. Disabled on a retained copy, whose
							    holder is not the controller, and under a legal hold, which is
							    a records decision that outranks custody. */}
							<button
								className="dbtn"
								type="button"
								disabled={blocked || !!doc.isRetainedCopy || legalHeld}
								title={
									blocked
										? 'Checkouts need the eVault add-on.'
										: doc.isRetainedCopy
											? RETAINED_COPY_CHECKOUT_HINT
											: legalHeld
												? 'Under legal hold — release it first'
												: 'Takes the sealed original out of the vault permanently'
								}
								onClick={() => onCheckout(doc)}
							>
								Check out
							</button>
						</>
					)}
					{/* ON EVERY DOCUMENT (product decision, 2026-09-22): the owner and
					    the holder of a retained copy both get a watermarked view — a
					    quick look at what was signed without handing out the original.
					    The server allows exactly those two (`canViewCopy`).
					    Not on a tombstone: `/vault-copies` answers 409 there, because
					    there is nothing left to watermark.

					    THERE IS NO "DOWNLOAD ORIGINAL" HERE, and there must not be one
					    (product decision, 23 Sep 2026): while a record is in the vault the
					    UI shows a watermarked copy and the certificate/evidence — never
					    the bare original. The sealed bytes leave the vault by exactly two
					    doors, both audited: a transfer notice, or a checkout (`Check out`
					    above, face-verified, custody ended). The button that used to sit
					    here was a stopgap from before checkout existed, so an owner could
					    run the certificate's Appendix A hash check; that check is done on
					    the checked-out file now. */}
					{doc.status !== 'destroyed' && !checkedOut ? (
						<button
							className="dbtn"
							type="button"
							disabled={!ready || !!busyId}
							title="Opens a watermarked copy in a new tab"
							onClick={() => void viewCopy(doc._id, doc.name)}
						>
							View document
						</button>
					) : null}
					<button className="dbtn" type="button" onClick={copyReference}>
						Copy reference
					</button>
				</div>
			</div>
		</Drawer>
	);
};

export default DocumentDrawer;
